package devxertech.com.pages.blogs.blogPages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import devxertech.com.theme.toSitePalette
import devxertech.com.utils.Constants
import devxertech.com.utils.Res
import devxertech.com.pages.blogs.NumberedArticlePoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2

@Composable
fun BlogBusiness(
    heading: String = "What Areas of Business Are Being Transformed by AI?",
    imageUrl: String = Res.Image.blog_area,
    points: List<NumberedArticlePoint> = listOf(
        NumberedArticlePoint("1.", "Smarter Decision-Making", "AI algorithms analyze real-time business data to forecast trends, recommend actions, and reduce risk. Leaders can make proactive decisions backed by evidence."),
        NumberedArticlePoint("2.", "Process Automation", "Tasks like document processing, email responses, customer service, and logistics operations are now handled by AI-powered tools increasing efficiency and reducing overhead."),
        NumberedArticlePoint("3.", "Customer Experience Personalization", "AI delivers tailored marketing, smart chatbots, and dynamic content based on user behavior. This leads to more satisfied and loyal customers."),
        NumberedArticlePoint("4.", "Security & Risk Management", "AI-driven systems detect and respond to cyber threats in milliseconds. They learn from new patterns and adapt faster than traditional security systems."),
        NumberedArticlePoint("5.", "Product & Service Innovation", "From generative design to predictive testing, AI helps companies develop better products faster, while minimizing cost and failure rates."),
    ),
) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = rememberBreakpoint()

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .justifyContent(JustifyContent.Center)
            .alignItems(AlignItems.Center)
            .gap(40.px)
            .flexWrap(FlexWrap.Wrap),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        // Left Column - Text
        Column(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint < Breakpoint.MD) 100.percent else if (breakpoint == Breakpoint.MD || breakpoint == Breakpoint.LG)
                    93.percent else 64.percent)
                .padding(20.px)
        ) {
            H2(attrs = { style { property("display", "flex"); property("margin", "0") } }) { Row (modifier = Modifier.gap(6.px).padding(bottom = 25.px),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically) {
                Image(src = Res.Image.blog_arrow, description = "", modifier = Modifier.size(15.px)
                    .color(palette.topBarMenuText.rgb).margin(bottom = if (breakpoint < Breakpoint.MD) 25.px else 0.px))
                SpanText(
                    text = heading,
                    modifier = Modifier
                        .fontSize(if (window.screen.width > 1600) 22.px else 20.px)
                        .fontWeight(FontWeight.Bold)
                        .color(palette.topBarMenuText.rgb)
                )
            } }
            // Right Column - Image
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center)
            ) {
                Image(
                    src = imageUrl,
                    description = heading,
                    modifier = Modifier
                        .width(if (breakpoint < Breakpoint.MD) 90.percent else 63.percent)
                )
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 5.percent)
            ) {

                points.forEach { point ->
                    AiPoint(number = point.number, title = point.title, description = point.description)
                }
            }
        }
    }
}

@Composable
private fun AiPoint(number: String, title: String, description: String) {
    val palette = ColorMode.current.toSitePalette()

    val numberWidth = 20.px // adjust to match your number width visually

    Column(modifier = Modifier.margin(bottom = 20.px)) {
        Row {
            SpanText(
                text = number,
                modifier = Modifier
                    .width(numberWidth)
                    .color(palette.primary.rgb)
                    .fontWeight(FontWeight.SemiBold)
                    .fontSize(if (window.screen.width > 1600) 22.px else 16.px)
            )
            SpanText(
                text = title,
                modifier = Modifier
                    .color(palette.primary.rgb)
                    .fontWeight(FontWeight.SemiBold)
                    .fontSize(if (window.screen.width > 1600) 22.px else 16.px)
            )
        }

        SpanText(
            text = description,
            modifier = Modifier
                .padding(left = numberWidth)
                .fontSize(if (window.screen.width > 1600) 18.px else 14.px)
                .fontFamily(Constants.FONT_FAMILY)
                .lineHeight(1.5)
                .color(palette.commonSecondaryButtonColor.rgb)
        )
    }
}
